<script lang="ts" setup>
import OzCheckBox from '@@/library/v4/components/OzCheckBox.vue'
import OzContainedButton, { OzContainedButtonColorScheme } from '@@/library/v4/components/OzContainedButton.vue'
import OzInput from '@@/library/v4/components/OzInput.vue'
import OzLabelInput from '@@/library/v4/components/OzLabelInput.vue'
import OzLoadingText from '@@/library/v4/components/OzLoadingText.vue'
import OzTextButton, { OzTextButtonColorScheme } from '@@/library/v4/components/OzTextButton.vue'
import { useLtiStore } from '@@/pinia/lti_store'
import { computed, ref } from 'vue'
import { dir } from '@@/bits/current_dir'
import { __ } from '@@/bits/intl'

const ltiStore = useLtiStore()

const isPasswordShown = ref(false)

const isUsernameValid = computed((): 'yes' | 'no' => {
  return ltiStore.usernameValidationMessage.length ? 'no' : 'yes'
})

const isPasswordValid = computed((): 'yes' | 'no' => {
  return ltiStore.passwordValidationMessage.length ? 'no' : 'yes'
})

const isLoginWithPasswordButtonDisabled = computed((): boolean => {
  return (
    !ltiStore.username.length ||
    !ltiStore.password.length ||
    !!ltiStore.usernameValidationMessage.length ||
    !!ltiStore.passwordValidationMessage.length
  )
})

function onInputUsernameInput(e): void {
  ltiStore.setUsername(e.target.value)

  // Reset the username validation message to enable the Continue button when typing on mobile and tablet
  if (ltiStore.usernameValidationMessage.length) ltiStore.setUsernameValidationMessage('')
}

function onInputPasswordInput(e): void {
  ltiStore.setPassword(e.target.value)

  // Reset the password validation message to enable the Login button when typing on mobile and tablet
  if (ltiStore.passwordValidationMessage.length) ltiStore.setPasswordValidationMessage('')
}
</script>

<template>
  <form novalidate class="flex flex-col" @submit.prevent="ltiStore.loginWithPassword">
    <OzLabelInput class="mb-2">
      <template #label>
        <label
          for="username-input"
          :class="['text-body-small-posts', 'text-dark-text-100 dark:text-light-text-100', 'px-2']"
        >
          {{ __('Email/username') }}
        </label>
      </template>

      <template #input
        ><OzInput
          id="username-input"
          name="username"
          type="email"
          autocomplete="username"
          :disabled="ltiStore.isAwaitingServerResponse"
          :model-value="ltiStore.username"
          :validation-message="ltiStore.usernameValidationMessage"
          placeholder="harrypotter@hogwarts.edu"
          :valid="isUsernameValid"
          class="bg-light-ui-100 dark:bg-dark-ui-100"
          @input="onInputUsernameInput"
      /></template>
    </OzLabelInput>
    <OzLabelInput class="mb-4">
      <template #label>
        <label
          for="password-input"
          :class="['text-body-small-posts', 'text-dark-text-100 dark:text-light-text-100', 'px-2']"
        >
          {{ __('Password') }}
        </label>
      </template>

      <template #input
        ><OzInput
          id="password-input"
          name="password"
          autocomplete="current-password"
          autocapitalize="none"
          :disabled="ltiStore.isAwaitingServerResponse"
          :type="isPasswordShown ? 'text' : 'password'"
          :model-value="ltiStore.password"
          :validation-message="ltiStore.passwordValidationMessage"
          :placeholder="__('Enter password')"
          :valid="isPasswordValid"
          class="bg-light-ui-100 dark:bg-dark-ui-100"
          @input="onInputPasswordInput"
      /></template>
      <template #focusedAfterInputAccessory>
        <OzCheckBox
          :value="isPasswordShown"
          :label="__('Show')"
          :aria-label="__('Show password as plain text. Warning: this will display your password on the screen.')"
          :dir="dir()"
          class="absolute top-0 end-2 px-2"
          @input="isPasswordShown = !isPasswordShown"
        />
      </template>
    </OzLabelInput>
    <p
      class="flex items-center justify-center tablet-portrait:justify-start px-2 text-body-small-posts text-dark-text-200 dark:text-light-text-200 mb-4"
    >
      <OzTextButton
        :href="ltiStore.forgotPasswordUrl"
        target="_blank"
        :color-scheme="OzTextButtonColorScheme.Secondary"
        :text="__('Forgot password?')"
        class="!underline font-semibold"
      />
    </p>
    <div class="flex justify-center tablet-portrait:justify-end">
      <OzLoadingText
        v-if="ltiStore.isAwaitingServerResponse"
        :text="__('Logging in...')"
        class="text-heading-4 text-center font-semibold px-5 py-2"
      />
      <OzContainedButton
        v-else
        :color-scheme="OzContainedButtonColorScheme.Primary"
        :disabled="isLoginWithPasswordButtonDisabled"
        class="w-full tablet-portrait:w-auto"
        >{{ __('Log in to connect') }}</OzContainedButton
      >
    </div>
  </form>
</template>
